import React, { useState, useEffect } from "react";
import { Link, useHistory } from "react-router-dom";
import helperFunction from "../../Function/helperFunction";
import { DataService } from "../../Services/dataService";
import ReactPaginate from "react-paginate";

export const QuotationsList = () => {
  const history = useHistory();
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [retailers, setRetailers] = useState([]);
  const [selectedRetailer, setSelectedRetailer] = useState("ALL");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    fetchQuotations(1, 10, selectedRetailer);
    fetchRetailers();
  }, []);

  const fetchRetailers = async () => {
    try {
      const res = await DataService.get("/admin/retailer_list");
      if (res.status && res.data) {
        setRetailers(res.data);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchQuotations = async (page = 1, limit = 10, retailerId = "ALL", search = searchTerm) => {
    try {
      setLoading(true);
      let url = `/admin/get_all_quotations?page=${page}&limit=${limit}`;
      if (retailerId !== "ALL") {
        url += `&user_id=${retailerId}`;
      }
      if (search && search.trim() !== "") {
        url += `&search=${encodeURIComponent(search)}`;
      }
      const res = await DataService.get(url);
      if (res.status && res.data) {
        setQuotations(res.data);
        if (res.pagination) {
          setPageCount(Math.ceil(res.pagination.totalRecord / res.pagination.limit));
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handlePageClick = ({ selected }) => {
    setCurrentPage(selected);
    fetchQuotations(selected + 1, 10, selectedRetailer, searchTerm);
  };

  const openDetails = (quote) => {
    setSelectedQuote(quote);
    setShowModal(true);
  };

  const exportToCSV = async () => {
    try {
      setLoading(true);
      let url = `/admin/get_all_quotations?page=1&limit=10000`;
      if (selectedRetailer !== "ALL") {
        url += `&user_id=${selectedRetailer}`;
      }
      if (searchTerm && searchTerm.trim() !== "") {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      const res = await DataService.get(url);
      if (res.status && res.data) {
        const dataForCsv = res.data.map(q => {
          const quoteToken = q.quote_token || q.job_token || 'N/A';
          const retailerName = q.retailer_detail ? q.retailer_detail.retailer_name || q.retailer_detail.first_name : 'Unknown Retailer';
          const retailerEmail = q.retailer_detail ? q.retailer_detail.email : '';
          const dateStr = q.created_at ? new Date(q.created_at).toLocaleDateString() : 'N/A';
          const status = q.quote_status || 'INQUIRY';
          
          const origin = q.packing ? (q.packing.origin || q.packing.origin_country || '') : '';
          const dest = q.packing ? (q.packing.destination || q.packing.destination_country || '') : '';
          const mode = q.selected_courier_type || 'NONE';
          
          let products = '';
          if (q.packing && q.packing.items && q.packing.items.length > 0) {
            products = q.packing.items.map(i => i.item_description || i.item_name).filter(Boolean).join(' | ');
          }
          
          const amount = q.packing ? (q.packing.goods_value || 0) : 0;

          // Wrap strings in quotes to prevent CSV breakage from commas in data
          return `"${quoteToken}","${dateStr}","${retailerName}","${retailerEmail}","${status}","${origin}","${dest}","${mode}","${products}","${amount}"`;
        });
        
        const headers = '"Quotation ID","Date","Retailer Name","Retailer Email","Status","Origin","Destination","Service Mode","Products","Total Amount"';
        const csvContent = [headers, ...dataForCsv].join('\n');
        
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement("a");
        const urlObj = URL.createObjectURL(blob);
        link.setAttribute("href", urlObj);
        link.setAttribute("download", `Quotations_Report_${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch(e) {
      console.error("Export error", e);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedIds(quotations.map(q => q._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleDelete = async (ids) => {
    if (!window.confirm(`Are you sure you want to delete ${ids.length} quotation(s)? This will delete the quotation and all its associated data.`)) return;
    try {
      setLoading(true);
      const res = await DataService.post('/admin/delete_quotations', { quote_ids: ids });
      if (res.status) {
        setSelectedIds([]);
        fetchQuotations(currentPage + 1, 10, selectedRetailer, searchTerm);
      } else {
        alert(res.message || 'Failed to delete quotations');
      }
    } catch(e) {
      console.error(e);
      alert('Error deleting quotations');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(`DANGER: Are you absolutely sure you want to delete ALL quotations in the system? This action cannot be undone and will delete all associated data.`)) return;
    try {
      setLoading(true);
      const res = await DataService.post('/admin/delete_quotations', { delete_all: true });
      if (res.status) {
        alert(res.message || 'All quotations deleted successfully');
        setSelectedIds([]);
        fetchQuotations(1, 10, selectedRetailer, searchTerm);
      } else {
        alert(res.message || 'Failed to delete all quotations');
      }
    } catch(e) {
      console.error(e);
      alert('Error deleting all quotations');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container-fluid">
      <div className="row mb-3">
        <div className="col-12 d-flex justify-content-between align-items-center">
          <h4 className="card-title">Quotations List</h4>
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search Quote ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setCurrentPage(0);
                    fetchQuotations(1, 10, selectedRetailer, searchTerm);
                  }
                }}
                style={{ width: '200px' }}
              />
              <button 
                className="btn btn-sm btn-primary ms-1"
                onClick={() => { setCurrentPage(0); fetchQuotations(1, 10, selectedRetailer, searchTerm); }}
              >
                Search
              </button>
            </div>
            <div className="d-flex align-items-center">
            <label className="me-2 mb-0 font-weight-bold">Filter by Retailer:</label>
            <select 
              className="form-select form-select-sm" 
              value={selectedRetailer} 
              onChange={(e) => { setSelectedRetailer(e.target.value); setCurrentPage(0); fetchQuotations(1, 10, e.target.value, searchTerm); }}
              style={{ width: '250px' }}
            >
              <option value="ALL">All Retailers</option>
              {retailers.map(r => (
                <option key={r._id} value={r._id}>{r.retailer_name} ({r.email})</option>
              ))}
            </select>
            <button 
              className="btn btn-sm btn-success ms-3"
              onClick={exportToCSV}
              disabled={loading}
            >
              <i className="fa fa-download me-1"></i> Export Report
            </button>
            {selectedIds.length > 0 && (
              <button 
                className="btn btn-sm btn-danger ms-2"
                onClick={() => handleDelete(selectedIds)}
                disabled={loading}
              >
                <i className="fa fa-trash me-1"></i> Delete Selected ({selectedIds.length})
              </button>
            )}
            <button 
              className="btn btn-sm btn-outline-danger ms-2 font-weight-bold"
              onClick={handleDeleteAll}
              disabled={loading}
              title="Delete all quotations in the system"
            >
              <i className="fa fa-exclamation-triangle me-1"></i> Delete All Quotes
            </button>
          </div>
          </div>
        </div>
      </div>
      <div className="row">
        <div className="col-12">
          <div className="card">
            <div className="card-body">
              <div className="table-responsive table-bordered">
                <table className="table table-striped table-nowrap mb-0">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input 
                          type="checkbox" 
                          className="form-check-input" 
                          onChange={handleSelectAll} 
                          checked={quotations.length > 0 && selectedIds.length === quotations.length}
                        />
                      </th>
                      <th>Quote ID</th>
                      <th>Date</th>
                      <th>Retailer Name</th>
                      <th>Status</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="6" className="text-center">Loading...</td>
                      </tr>
                    ) : quotations.length > 0 ? (
                      quotations.map((q, idx) => {
                        const quoteToken = q.quote_token || 'N/A';
                        const status = q.quote_status || 'INQUIRY';
                        const retailerName = q.retailer_detail ? q.retailer_detail.retailer_name || q.retailer_detail.first_name : 'Unknown Retailer';
                        const retailerEmail = q.retailer_detail ? q.retailer_detail.email : '';
                        const dateStr = q.created_at ? new Date(q.created_at).toLocaleDateString() : 'N/A';

                        return (
                          <tr key={q._id || idx}>
                            <td className="align-middle">
                              <input 
                                type="checkbox" 
                                className="form-check-input" 
                                checked={selectedIds.includes(q._id)} 
                                onChange={() => handleSelect(q._id)} 
                              />
                            </td>
                            <td className="text-body align-middle font-weight-bold">{quoteToken}</td>
                            <td className="text-body align-middle">{dateStr}</td>
                            <td className="text-body align-middle">
                              {retailerName} <br />
                              <small className="text-muted">{retailerEmail}</small>
                            </td>
                            <td className="align-middle">
                              <span className={`badge px-2 py-1 ${status === 'JOB' ? 'bg-success text-white' : 'bg-warning text-dark'}`}>
                                {status}
                              </span>
                            </td>
                            <td className="align-middle">
                              <div className="d-flex gap-2">
                                <button 
                                  onClick={() => {
                                    history.push({
                                      pathname: '/rate-checker',
                                      state: { prefillQuote: q }
                                    });
                                  }}
                                  className="btn btn-sm btn-outline-info waves-effect waves-light"
                                  title="Check Rate"
                                >
                                  <i className="bx bx-calculator"></i> Rate Check
                                </button>
                                <button 
                                  onClick={() => openDetails(q)}
                                  className="btn btn-sm btn-outline-primary waves-effect waves-light"
                                >
                                  View Details
                                </button>
                                <button 
                                  onClick={() => handleDelete([q._id])}
                                  className="btn btn-sm btn-outline-danger waves-effect waves-light"
                                  title="Delete Quotation"
                                >
                                  <i className="fa fa-trash"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="6" className="text-center">No quotations found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
              
              {pageCount > 1 && (
                <div className="d-flex justify-content-end mt-3">
                  <ReactPaginate
                    previousLabel={"Previous"}
                    nextLabel={"Next"}
                    pageCount={pageCount}
                    onPageChange={handlePageClick}
                    containerClassName={"pagination pagination-sm"}
                    pageClassName={"page-item"}
                    pageLinkClassName={"page-link"}
                    previousClassName={"page-item"}
                    previousLinkClassName={"page-link"}
                    nextClassName={"page-item"}
                    nextLinkClassName={"page-link"}
                    activeClassName={"active"}
                    forcePage={currentPage}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {showModal && selectedQuote && (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: '8px', width: '90%', maxWidth: '800px', maxHeight: '90vh', overflowY: 'auto', padding: '24px', position: 'relative' }}>
            <button 
              onClick={() => setShowModal(false)}
              style={{ position: 'absolute', top: '16px', right: '16px', border: 'none', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer' }}
            >
              &times;
            </button>
            <div className="d-flex justify-content-between align-items-center mb-4 pe-5">
              <h4 className="mb-0">Quote Details: {selectedQuote.quote_token || 'N/A'}</h4>
              <button 
                onClick={() => {
                  history.push({
                    pathname: '/rate-checker',
                    state: { prefillQuote: selectedQuote }
                  });
                }}
                className="btn btn-sm btn-primary d-flex align-items-center"
              >
                <i className="bx bx-calculator me-1"></i> Check Rate
              </button>
            </div>
            <div className="row">
              <div className="col-md-6 mb-3">
                <strong>Status: </strong> {selectedQuote.quote_status}
              </div>
              <div className="col-md-6 mb-3">
                <strong>Date: </strong> {selectedQuote.created_at ? new Date(selectedQuote.created_at).toLocaleString() : 'N/A'}
              </div>
              <div className="col-md-6 mb-3">
                <strong>Quote By: </strong> {selectedQuote.quote_by}
              </div>
              <div className="col-md-6 mb-3">
                <strong>Selected Courier: </strong> {selectedQuote.selected_courier_type || 'N/A'}
              </div>
            </div>
            

            <h5 className="mt-3 border-bottom pb-2">Shipment Details</h5>
            <div className="row mb-4">
              <div className="col-md-6">
                <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', height: '100%' }}>
                  <h6 style={{ color: '#6c757d', marginBottom: '12px' }}><i className="bx bx-map"></i> Route</h6>
                  <div className="d-flex justify-content-between">
                    <div>
                      <small className="text-muted d-block">Origin</small>
                      <strong>{selectedQuote.packing?.origin || 'N/A'}</strong>
                    </div>
                    <div className="text-center px-3">
                      <i className="bx bx-right-arrow-alt" style={{ fontSize: '1.5rem', color: '#cbd5e1' }}></i>
                    </div>
                    <div className="text-end">
                      <small className="text-muted d-block">Destination</small>
                      <strong>{selectedQuote.packing?.destination || 'N/A'}</strong>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-md-6">
                <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px', height: '100%' }}>
                  <h6 style={{ color: '#6c757d', marginBottom: '12px' }}><i className="bx bx-package"></i> Cargo Specs</h6>
                  <div className="d-flex justify-content-between">
                    <div>
                      <small className="text-muted d-block">Total Packages</small>
                      <strong>{selectedQuote.packing?.number_of_packages || 'N/A'}</strong>
                    </div>
                    <div>
                      <small className="text-muted d-block">Total Weight (KG)</small>
                      <strong>{selectedQuote.packing?.weight || 'N/A'}</strong>
                    </div>
                    <div>
                      <small className="text-muted d-block">Total Volume (CBM)</small>
                      <strong>{selectedQuote.packing?.volume || 'N/A'}</strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <h5 className="mt-3 border-bottom pb-2">Item Breakdown</h5>
            <div className="table-responsive mb-4">
              <table className="table table-sm table-bordered">
                <thead className="table-light">
                  <tr>
                    <th>Package</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Weight (KG)</th>
                    <th>Dimensions (L x W x H)</th>
                    <th>Volume (CBM)</th>
                    <th>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {selectedQuote.packing?.items && selectedQuote.packing.items.length > 0 ? (
                    selectedQuote.packing.items.map((item, idx) => (
                      <tr key={idx}>
                        <td>Package {idx + 1}</td>
                        <td>{item.no_of_pkgs || 1}</td>
                        <td>{item.price ? `${item.price} AED` : 'N/A'}</td>
                        <td>{item.total_weight_KG || item.dimension?.kilogram || 'N/A'}</td>
                        <td>
                          {item.dimension?.length || 0} x {item.dimension?.width || 0} x {item.dimension?.height || 0}
                        </td>
                        <td>{item.total_volume_CBM ? Number(item.total_volume_CBM).toFixed(4) : 'N/A'}</td>
                        <td>Part of {item.item_description || 'Product'}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center text-muted">No items found</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            
            {selectedQuote.selected_courier_type && selectedQuote.selected_courier_type !== 'NONE' && (
              <>
                <h5 className="mt-4 border-bottom pb-2">Cost Breakdown</h5>
                <div className="row mb-4">
                  <div className="col-md-6 offset-md-6">
                    <div style={{ background: '#f8f9fa', padding: '16px', borderRadius: '8px' }}>
                      <table className="table table-sm table-borderless mb-0">
                        <tbody>
                          <tr>
                            <td className="text-muted">Shipping Mode</td>
                            <td className="text-end fw-bold">{selectedQuote.selected_courier_type}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Base Freight Cost</td>
                            <td className="text-end fw-bold">AED {parseFloat(selectedQuote.packing?.base_freight || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td className="text-success">Discount</td>
                            <td className="text-end fw-bold text-success">- AED {parseFloat(selectedQuote.packing?.discount || 0).toFixed(2)}</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Duties & Taxes</td>
                            <td className="text-end fw-bold">AED 0.00</td>
                          </tr>
                          <tr>
                            <td className="text-muted">Transit Insurance</td>
                            <td className="text-end fw-bold">AED {parseFloat(selectedQuote.packing?.insurance || 0).toFixed(2)}</td>
                          </tr>
                          <tr style={{ borderTop: '2px solid #dee2e6' }}>
                            <td className="pt-3 fs-5 fw-bold text-dark">Total Due</td>
                            <td className="pt-3 text-end fs-5 fw-bold text-success">AED {parseFloat(selectedQuote.packing?.total_due || 0).toFixed(2)}</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}

          </div>
        </div>
      )}
    </div>
  );
};
